import * as React from 'react';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { DropViewState, createDropViewState } from 'owa-dnd';
import type { Subtodo } from 'owa-todo-types';
import { useTextChangeCallback } from 'owa-timestream-asset-sdk';
import { createSubtasks } from 'owa-timestream-asset-sdk-tasks';
import { useOnDrop, canDropSubtask, useOnDragOver, useOnDragLeave } from './subtaskDroppable';
import type { SubTasksViewProps } from '../schema';
import { useOnBlurUpdate } from 'owa-react-hooks/lib/useOnBlurUpdate';

export default function useAddSubTaskViewState({
    taskId,
    newSubtaskRef,
    disableSubtasks,
}: SubTasksViewProps) {
    const [dropViewState, setDropViewState] = React.useState<DropViewState>(createDropViewState());
    const [newSubtaskSubject, setNewSubtaskSubject] = React.useState<string>('');
    const [uncreatedSubtasks, setUncreatedSubtasks] = React.useState<Partial<Subtodo>[]>([]);

    const onDrop = useOnDrop(taskId);
    const onDragOver = useOnDragOver(setDropViewState);
    const onDragLeave = useOnDragLeave(setDropViewState);

    const onTextFieldChange = useTextChangeCallback(setNewSubtaskSubject);
    const newSubtask = React.useCallback(() => {
        const subtask = { Subject: newSubtaskSubject };
        if (taskId && newSubtaskSubject) {
            createSubtasks(taskId, [subtask]);
            setNewSubtaskSubject('');
        } else if (!taskId && newSubtaskSubject) {
            setUncreatedSubtasks(uncreatedSubtasks.concat(subtask));
            setNewSubtaskSubject('');
        }
    }, [newSubtaskSubject, taskId]);

    const onBlur = useOnBlurUpdate(newSubtask);

    const onKeyDown = React.useCallback(
        (ev: React.KeyboardEvent) => {
            switch (ev.keyCode) {
                case KeyCodes.enter:
                    ev.preventDefault();
                    ev.stopPropagation();
                    newSubtask();
                    break;
                case KeyCodes.escape:
                    setNewSubtaskSubject('');
                    break;
            }
        },
        [newSubtaskSubject, taskId]
    );

    React.useEffect(() => {
        if (taskId && uncreatedSubtasks.length > 0) {
            createSubtasks(taskId, uncreatedSubtasks);
            setUncreatedSubtasks([]);
        }
    }, [taskId, uncreatedSubtasks]);

    return {
        textFieldProps: {
            componentRef: newSubtaskRef,
            onBlur: onBlur,
            onChange: onTextFieldChange,
            onKeyDown: onKeyDown,
            value: newSubtaskSubject,
            disabled: !!disableSubtasks,
        },
        droppableProps: {
            dropViewState: dropViewState,
            onDrop: onDrop,
            canDrop: canDropSubtask,
            onDragOver: onDragOver,
            onDragLeave: onDragLeave,
        },
    };
}
